import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import WaitlistForm from '../forms/WaitlistForm';

const FAQSection = () => {
  const faqs = [
    {
      question: "When will Wabisabi launch?",
      answer: "We're launching our beta web app in August 2025 with 1,000 testing spots. Beta users will help us perfect the experience before our full public launch."
    },
    {
      question: "How does the waitlist work?",
      answer: "Join the waitlist to secure your spot among the first 1,000 users. We'll notify you when beta access is ready and guide you through the setup process."
    },
    {
      question: "What do I get as a beta user?",
      answer: "The first 1,000 waitlist members get exclusive beta access plus 3 months of free premium features to test and provide feedback."
    },
    {
      question: "Is this only for couples?",
      answer: "No! Wabisabi works for all your relationships — partners, family, friends, colleagues, and anyone you want to stay connected with."
    },
    {
      question: "What if I'm not in the first 1,000?",
      answer: "You'll still get early access ahead of the general public, plus we'll keep you updated with relationship tips and product development insights."
    },
    {
      question: "Is my data safe?",
      answer: "Yes, your data is protected with multiple layers of security. We use Supabase's enterprise-grade infrastructure with secure authentication (including Google and Apple sign-in), encrypted connections, and Row Level Security that ensures you can only access your own data. Your files are stored in isolated buckets with strict access controls. We never sell your personal information, and you maintain complete control to view, edit, or delete your data anytime. All sensitive information is encrypted in transit, and we follow security best practices including secure session management and input validation."
    },
    {
      question: "Will this cost anything during beta?",
      answer: "Beta access is completely free for 3 months. This gives you time to experience Wabisabi and see how it improves your relationships before any paid features."
    },
    {
      question: "What devices will it work on?",
      answer: "The beta launches as a web app that works on any device with a browser — desktop, tablet, or mobile. We plan to launch our mobile app by August 2025 for both android and iOS."
    }
  ];

  return (
    <section className="section-padding bg-white relative overflow-hidden mx-[20px] md:mx-0 rounded-[8px] md:rounded-none">
      <div className="relative z-10 mx-[20px] md:max-w-7xl md:mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h2
              className="font-bold text-[#6C4F77] responsive-title leading-none"
              style={{
                fontSize: '28px',
                letterSpacing: '-1px',
                verticalAlign: 'middle'
              }}
            >
              <style dangerouslySetInnerHTML={{
                __html: `
                  @media (min-width: 768px) {
                    .responsive-title {
                      font-size: 60px !important;
                      letter-spacing: -2px !important;
                    }
                  }
                `
              }} />
              FAQs
            </h2>
          </div>
          <p className="text-[#2C2C2C]/80 max-w-2xl mx-auto" style={{ fontSize: '14px', fontFamily: 'Euclid Circular A' }}>
            <style dangerouslySetInnerHTML={{
              __html: `
                @media (min-width: 768px) {
                  .text-center p {
                    font-size: 1.25rem !important;
                  }
                }
              `
            }} />
            Everything you need to know about joining the Wabisabi waitlist
          </p>

          <div className="mt-8">
            <WaitlistForm />
          </div>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="w-full space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="border border-[#2C2C2C4D] rounded-[8px] px-6 py-2"
            >
              <AccordionTrigger className="text-left hover:no-underline py-6">
                <span className="text-[#6C4F77] pr-4" style={{
                  fontFamily: 'Euclid Circular A',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  <style dangerouslySetInnerHTML={{
                    __html: `
                      @media (min-width: 768px) {
                        .text-left span {
                          font-size: 1.5rem !important;
                        }
                      }
                    `
                  }} />
                  {faq.question}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <p className="text-[#2C2C2C]" style={{
                  fontFamily: 'Euclid Circular A',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '1.6'
                }}>
                  <style dangerouslySetInnerHTML={{
                    __html: `
                      @media (min-width: 768px) {
                        .pb-6 p {
                          font-size: 1.5rem !important;
                        }
                      }
                    `
                  }} />
                  {faq.answer}
                </p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FAQSection;
